import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Grid,
  Chip,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import {
  fetchOrderHistory,
  resendOtp,
  manualDeliveryOverride,
  getOrderSmsStatus,
} from '../../services/api';

const getStatusColor = (statusName) => {
  switch (statusName?.toLowerCase()) {
    case 'received':
      return 'info';
    case 'dispatched':
      return 'warning';
    case 'delivered':
      return 'success';
    default:
      return 'default';
  }
};

const OrderDetailsModal = ({ open, onClose, order, onOrderUpdated }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const [isResendingOtp, setIsResendingOtp] = useState(false);
  const [resendOtpMessage, setResendOtpMessage] = useState('');
  const [resendOtpError, setResendOtpError] = useState('');

  const [showManualOverride, setShowManualOverride] = useState(false);
  const [receiverName, setReceiverName] = useState('');
  const [isManualOverriding, setIsManualOverriding] = useState(false);
  const [manualOverrideMessage, setManualOverrideMessage] = useState('');
  const [manualOverrideError, setManualOverrideError] = useState('');

  const [smsStatus, setSmsStatus] = useState(null);
  const [isLoadingSmsStatus, setIsLoadingSmsStatus] = useState(false);

  const userRole = localStorage.getItem('userRole');

  const fetchHistory = useCallback(async () => {
    if (!order?.id) return;
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const response = await fetchOrderHistory(order.id);
      setHistory(response.data);
    } catch (err) {
      setHistoryError(err.response?.data?.message || 'Failed to fetch order history');
    } finally {
      setIsLoadingHistory(false);
    }
  }, [order?.id]);

  const fetchSmsStatus = useCallback(async () => {
    if (!order?.id) return;
    setIsLoadingSmsStatus(true);
    try {
      const response = await getOrderSmsStatus(order.id);
      setSmsStatus(response.data);
    } catch (err) {
      setSmsStatus(null);
    } finally {
      setIsLoadingSmsStatus(false);
    }
  }, [order?.id]);

  useEffect(() => {
    if (open && order) {
      fetchHistory();
      fetchSmsStatus();
      setResendOtpMessage('');
      setResendOtpError('');
      setShowManualOverride(false);
      setReceiverName('');
      setManualOverrideMessage('');
      setManualOverrideError('');
    }
  }, [open, order, fetchHistory, fetchSmsStatus]);

  const handleResendOtp = async () => {
    if (!order?.id) return;
    setIsResendingOtp(true);
    setResendOtpMessage('');
    setResendOtpError('');
    try {
      const response = await resendOtp(order.id);
      setResendOtpMessage(response.data.message || 'New OTP sent successfully!');
    } catch (err) {
      setResendOtpError(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setIsResendingOtp(false);
    }
  };

  const handleManualDeliveryOverride = async () => {
    if (!order?.id) return;
    setIsManualOverriding(true);
    setManualOverrideMessage('');
    setManualOverrideError('');
    try {
      const response = await manualDeliveryOverride(order.id, receiverName);
      setManualOverrideMessage(response.data.message || 'Order manually marked as delivered.');
      setShowManualOverride(false);
      setReceiverName('');
      if (onOrderUpdated) {
        onOrderUpdated();
      }
    } catch (error) {
      const errorData = error.response?.data || {};
      const errorCode = errorData.error || 'UNKNOWN_ERROR';
      const baseMessage =
        errorData.message || error.message || 'Failed to perform manual delivery override.';

      let detailedMessage = baseMessage;

      if (errorCode === 'INVALID_ORDER_STATUS') {
        detailedMessage += `\n\nCurrent Status: ${errorData.currentStatusName || 'Unknown'}\nRequired Status: ${errorData.allowedStatusNames?.join(' or ') || 'Received or Dispatched'}`;
      } else if (errorCode === 'SMS_NOT_FAILED') {
        detailedMessage += `\n\nSMS Status: ${errorData.smsStatus || 'Unknown'}\nSMS Code: ${errorData.smsStatusCode || 'N/A'}\nReason: ${errorData.failureReason || 'No failure reason provided'}`;
      } else if (errorCode === 'STATUS_CONFIG_ERROR') {
        detailedMessage += `\n\nDetails: ${errorData.details || 'System configuration issue'}`;
      } else if (errorData.details) {
        detailedMessage += `\n\nDetails: ${errorData.details}`;
      }

      setManualOverrideError(detailedMessage);
    } finally {
      setIsManualOverriding(false);
    }
  };

  if (!order) return null;

  const currentStatusName =
    history.length > 0
      ? history[0].OrderStatus.status_name.toLowerCase()
      : order.status?.status_name?.toLowerCase() || '';

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={fullScreen}
    >
      <AppDialogTitle onClose={onClose} subtitle="View and update order information">
        Order Details
      </AppDialogTitle>

      <AppDialogContent>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Waybill No
            </Typography>
            <Typography variant="body1">{order.waybill_no}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Order No
            </Typography>
            <Typography variant="body1">{order.order_no || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Customer
            </Typography>
            <Typography variant="body1">{order.customer_name}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Phone
            </Typography>
            <Typography variant="body1">{order.customer_phone}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Address
            </Typography>
            <Typography variant="body1">{order.customer_address}</Typography>
          </Grid>
          {order.status?.status_name && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={order.status.status_name}
                color={getStatusColor(order.status.status_name)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>
          )}
        </Grid>

        {(userRole === 'clerk' || userRole === 'cus-admin' || userRole === 'super-admin') &&
          currentStatusName === 'dispatched' && (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="contained"
                onClick={handleResendOtp}
                disabled={isResendingOtp}
                fullWidth={fullScreen}
              >
                {isResendingOtp ? 'Resending OTP...' : 'Resend OTP to Customer'}
              </Button>
              {resendOtpMessage && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  {resendOtpMessage}
                </Alert>
              )}
              {resendOtpError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {resendOtpError}
                </Alert>
              )}
            </Box>
          )}

        {userRole === 'cus-admin' &&
          (currentStatusName === 'dispatched' || currentStatusName === 'received') && (
            <>
              {isLoadingSmsStatus ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Checking SMS delivery status...
                  </Typography>
                </Box>
              ) : smsStatus && smsStatus.sms_status === 'Failed' ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 3,
                    bgcolor: 'warning.50',
                    borderColor: 'warning.light',
                  }}
                >
                  <Typography variant="subtitle1" color="warning.dark" gutterBottom>
                    Manual Delivery Override
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    SMS delivery has failed for this order. Use this option to manually mark the
                    order as delivered when SMS cannot be delivered to the customer.
                  </Typography>

                  {!showManualOverride ? (
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => setShowManualOverride(true)}
                      fullWidth={fullScreen}
                    >
                      Manual Override Delivery
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <TextField
                        fullWidth
                        label="Receiver Name (Optional)"
                        value={receiverName}
                        onChange={(e) => setReceiverName(e.target.value)}
                        placeholder="Enter receiver name or leave blank"
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                        }}
                      >
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleManualDeliveryOverride}
                          disabled={isManualOverriding}
                          fullWidth
                        >
                          {isManualOverriding ? 'Processing...' : 'Confirm Manual Delivery'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setShowManualOverride(false);
                            setReceiverName('');
                            setManualOverrideError('');
                          }}
                          fullWidth
                        >
                          Cancel
                        </Button>
                      </Box>
                      {manualOverrideMessage && (
                        <Alert severity="success">{manualOverrideMessage}</Alert>
                      )}
                      {manualOverrideError && (
                        <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
                          {manualOverrideError}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Paper>
              ) : null}
            </>
          )}

        <Typography variant="h6" gutterBottom>
          Order Timeline
        </Typography>

        {isLoadingHistory ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : historyError ? (
          <Alert severity="error">{historyError}</Alert>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {history.map((log) => (
              <Paper key={log.id} variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Box>
                    <Chip
                      label={
                        log.OrderStatus.status_name.charAt(0).toUpperCase() +
                        log.OrderStatus.status_name.slice(1)
                      }
                      color={getStatusColor(log.OrderStatus.status_name)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Updated by: {log.User.name || log.User.username}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(log.created_at).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </AppDialogContent>

      <AppDialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit" fullWidth={fullScreen}>
          Close
        </Button>
      </AppDialogActions>
    </AppDialog>
  );
};

export default OrderDetailsModal;
